import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { paymentsAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const { clearCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }

    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 5;
    
    try {
      const response = await paymentsAPI.checkStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        clearCart();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else if (attempts < maxAttempts) {
        // Continue polling
        setAttempts(attempts + 1);
        setTimeout(checkPaymentStatus, 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Payment status error:', error);
      setStatus('error');
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto mt-12">
        <Card>
          <CardContent className="pt-6 text-center py-12">
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
                <h2 className="text-2xl font-bold mb-2">Processing Payment</h2>
                <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
                <p className="text-muted-foreground mb-6">Thank you for your purchase. Your services are now active.</p>
                <Button onClick={() => navigate('/my-services')} data-testid="view-my-services-button">
                  View My Services
                </Button>
              </>
            )}

            {(status === 'expired' || status === 'timeout' || status === 'error') && (
              <>
                <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-2">Payment Issue</h2>
                <p className="text-muted-foreground mb-6">
                  {status === 'expired' && 'Your payment session has expired.'}
                  {status === 'timeout' && 'Payment verification timed out. Please check your email for confirmation.'}
                  {status === 'error' && 'An error occurred. Please contact support if you were charged.'}
                </p>
                <div className="flex gap-4 justify-center">
                  <Button variant="outline" onClick={() => navigate('/services')}>
                    Browse Services
                  </Button>
                  <Button onClick={() => navigate('/support')}>
                    Contact Support
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
